import { useContext, useState } from 'react';
import { View, Text, TouchableOpacity, ScrollView } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import SheetModal from '@/components/General/SheetModal';
import SectionTitle from '@/components/General/SectionTitle';
import Unicon from '@/components/General/Unicon';
import IconButton from '@/components/General/IconButton';
import SortableList from '@/components/General/SortableList';
import { GuideContext } from '@/components/Guides/GuideProvider';
import { getGuide } from '@/config/guides';
import { saveDashboardSections, DEFAULT_DASHBOARD_SECTIONS } from '@/data/settings';
import { useQueryClient } from '@tanstack/react-query';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import Toggle from '@/components/General/inputs/Toggle';
import Toast from 'react-native-toast-message';

const SECTION_LABELS = {
    organizationSetup: 'Organization Setup',
    recentlyVisited: 'Recently Visited',
    mySmallGroups: 'My Small Groups',
    tasksNeedingAssigned: 'Tasks I Need To Assign',
    myUpcomingTasks: 'My Upcoming Tasks',
    upcomingEvents: 'Events',
    myTraining: 'My Training',
    myBoardsFeed: 'My Boards Feed',
    activeCare: 'Active Care',
};

const SECTION_ICONS = {
    organizationSetup: 'sparkles-outline',
    recentlyVisited: 'time-outline',
    mySmallGroups: 'book-outline',
    tasksNeedingAssigned: 'person-add-outline',
    myUpcomingTasks: 'checkbox-outline',
    upcomingEvents: 'calendar-outline',
    myTraining: 'school-outline',
    myBoardsFeed: 'newspaper-outline',
    activeCare: 'heart-outline',
};

const DashboardSettingsModal = ({ isOpen, onClose, shortcuts = [], onManageShortcuts, sections = DEFAULT_DASHBOARD_SECTIONS, loggedInUser, setupOnly = false }) => {
    const [isReordering, setIsReordering] = useState(false);
    const [loading, setLoading] = useState(false);
    const queryClient = useQueryClient();
    const guide = useContext(GuideContext);
    const sectionsKey = QueryKeys.dashboardSections.get(loggedInUser?.organizationID, loggedInUser?.accountID);

    const sortedSections = Object.entries(sections)
        .filter(([key]) => {
            if (setupOnly && key !== 'organizationSetup') return false;
            if (key === 'organizationSetup' && !(loggedInUser?.isAdmin || loggedInUser?.isAccountOwner)) return false;
            if (key === 'mySmallGroups' && !loggedInUser?.can?.('View Small Groups')) return false;
            if (key === 'upcomingEvents' && !loggedInUser?.can?.('View Events')) return false;
            if (key === 'myTraining' && !loggedInUser?.can?.('View Training')) return false;
            if (key === 'activeCare' && !loggedInUser?.can?.('View Follow Up')) return false;
            return true;
        })
        .sort((a, b) => a[1].order - b[1].order);

    const sortableSections = sortedSections.map(([key]) => ({
        id: key,
        label: SECTION_LABELS[key],
        icon: SECTION_ICONS[key],
    }));

    const toggleSection = async (sectionKey) => {
        const currentSections = queryClient.getQueryData(sectionsKey) || sections;
        const newVisibility = !currentSections[sectionKey]?.visible;

        // Optimistic update on both the legacy key and the dashboardData key
        const newSections = {
            ...currentSections,
            [sectionKey]: {
                ...currentSections[sectionKey],
                visible: newVisibility
            }
        };
        queryClient.setQueryData(sectionsKey, newSections);
        queryClient.setQueryData(QueryKeys.dashboardData.byPersonId(loggedInUser?.id), (old) => old ? { ...old, sections: newSections } : old);

        try {
            await saveDashboardSections(newSections);
        } catch {
            // Revert optimistic update on error
            queryClient.invalidateQueries({ queryKey: QueryKeys.dashboardSections.get() });
            queryClient.invalidateQueries({ queryKey: QueryKeys.dashboardData.byPersonId(loggedInUser?.id) });
            Toast.show({ type: 'error', text2: 'Failed to save settings' });
        }
    };

    const handleReorderConfirm = async (reorderedItems) => {
        setLoading(true);
        try {
            const currentSections = queryClient.getQueryData(sectionsKey) || sections;
            const newSections = { ...currentSections };
            reorderedItems.forEach((item, index) => {
                newSections[item.id] = {
                    ...newSections[item.id],
                    order: index,
                };
            });

            queryClient.setQueryData(sectionsKey, newSections);
            queryClient.setQueryData(QueryKeys.dashboardData.byPersonId(loggedInUser?.id), (old) => old ? { ...old, sections: newSections } : old);

            await saveDashboardSections(newSections);
            Toast.show({ type: 'success', text2: 'Sections reordered' });
        } catch {
            queryClient.invalidateQueries({ queryKey: QueryKeys.dashboardSections.get() });
            queryClient.invalidateQueries({ queryKey: QueryKeys.dashboardData.byPersonId(loggedInUser?.id) });
            Toast.show({ type: 'error', text2: 'Failed to reorder sections' });
        }
        setLoading(false);
    };

    const renderSortableItem = (item) => (
        <View style={styles.sortableItemContent}>
            <View style={styles.sortableIconContainer}>
                <Unicon name={item.icon} size={18} colorVariant="primary" />
            </View>
            <Text style={styles.sortableItemLabel}>{item.label}</Text>
        </View>
    );

    const resetToDefaults = async () => {
        try {
            await saveDashboardSections(DEFAULT_DASHBOARD_SECTIONS);
            queryClient.invalidateQueries({ queryKey: QueryKeys.dashboardSections.get() });
            queryClient.invalidateQueries({ queryKey: QueryKeys.dashboardData.byPersonId(loggedInUser?.id) });
            Toast.show({ type: 'success', text2: 'Settings reset to defaults' });
        } catch {
            Toast.show({ type: 'error', text2: 'Failed to reset settings' });
        }
    };

    return (
        <SheetModal
            isOpen={isOpen}
            onClose={onClose}
            title="Dashboard Settings"
            cancelText="Done"
            loading={loading}
            headerRight={guide?.accountID && getGuide('dashboard.basics') ? (
                <IconButton
                    iconName="help-circle-outline"
                    testID="dashboard-tour-button"
                    tooltipText="Tour this page"
                    onPress={() => {
                        // Queue the replay before closing — the provider
                        // starts it once this sheet's blocker clears.
                        guide.replay('dashboard.basics');
                        onClose();
                    }}
                />
            ) : null}
        >
            <ScrollView showsVerticalScrollIndicator={false} keyboardShouldPersistTaps="handled"
>
                <View style={styles.section}>
                    <View style={styles.sectionHeader}>
                        <SectionTitle title="Dashboard Sections" marginLeft={0} marginTop={0} />
                        {sortableSections.length > 1 && (
                            <TouchableOpacity
                                onPress={() => setIsReordering(true)}
                                style={styles.reorderButton}
                            >
                                <Unicon name="reorder-four-outline" size={18} colorVariant="primary" />
                                <Text style={styles.reorderButtonText}>Reorder</Text>
                            </TouchableOpacity>
                        )}
                    </View>
                    <Text style={styles.helpText}>Toggle which sections appear on your dashboard</Text>
                    
                    {sortedSections.map(([key, config]) => (
                        <View key={key} style={styles.sectionRow}>
                            <View style={styles.sectionInfo}>
                                <View style={styles.iconContainer}>
                                    <Unicon name={SECTION_ICONS[key]} size={18} colorVariant="primary" />
                                </View>
                                <Text style={styles.sectionLabel}>{SECTION_LABELS[key]}</Text>
                            </View>
                            <Toggle
                                testID={`dashboard-section-toggle-${key}`}
                                value={config.visible}
                                onValueChange={() => toggleSection(key)}
                            />
                        </View>
                    ))}
                </View>

                {!setupOnly && <View style={styles.section}>
                    <SectionTitle title="Shortcuts" marginLeft={0} marginTop={0} />
                    <Text style={styles.helpText}>Quick access to your favorite pages</Text>
                    
                    <TouchableOpacity style={styles.manageButton} onPress={onManageShortcuts}>
                        <Unicon name="add-circle-outline" size={20} colorVariant="primary" />
                        <Text style={styles.manageButtonText}>Manage Shortcuts</Text>
                        <Unicon name="chevron-forward-outline" size={16} colorVariant="textSecondary" />
                    </TouchableOpacity>
                    
                    {shortcuts.length > 0 && (
                        <Text style={styles.shortcutCount}>
                            {shortcuts.length} shortcut{shortcuts.length !== 1 ? 's' : ''} configured
                        </Text>
                    )}
                </View>}

                <TouchableOpacity style={styles.resetButton} onPress={resetToDefaults}>
                    <Unicon name="refresh-outline" size={18} colorVariant="error" />
                    <Text style={styles.resetButtonText}>Reset to Defaults</Text>
                </TouchableOpacity>
            </ScrollView>

            <SortableList
                items={sortableSections}
                itemContent={renderSortableItem}
                isOpen={isReordering}
                close={() => setIsReordering(false)}
                onConfirm={handleReorderConfirm}
                title="Reorder Sections"
                itemHeight={50}
                helpText="Drag to reorder your dashboard sections"
            />
        </SheetModal>
    );
};

const styles = StyleSheet.create(({ colors, fonts, shadows }) => ({
    section: {
        marginBottom: 24,
        paddingHorizontal:2
    },
    sectionHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 8,
    },
    reorderButton: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        paddingVertical: 4,
        paddingHorizontal: 8,
    },
    reorderButtonText: {
        fontSize: 13,
        fontFamily: fonts.body,
        color: colors.primary,
    },
    sortableItemContent: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    sortableIconContainer: {
        width: 32,
        height: 32,
        borderRadius: 16,
        backgroundColor: colors.primaryContainer,
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: 12,
    },
    sortableItemLabel: {
        fontSize: 14,
        fontFamily: fonts.body,
        color: colors.text,
    },
    helpText: {
        fontSize: 12,
        fontFamily: fonts.body,
        color: colors.textSecondary,
        marginBottom: 12,
    },
    sectionRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingVertical: 12,
        paddingHorizontal: 12,
        backgroundColor: colors.surface,
        boxShadow: shadows.card,
        borderRadius: 8,
        marginBottom: 8,
    },
    sectionInfo: {
        flexDirection: 'row',
        alignItems: 'center',
        flex: 1,
    },
    iconContainer: {
        width: 32,
        height: 32,
        borderRadius: 16,
        backgroundColor: colors.primaryContainer,
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: 12,
    },
    sectionLabel: {
        fontSize: 14,
        fontFamily: fonts.body,
        color: colors.text,
    },
    manageButton: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 14,
        paddingHorizontal: 12,
        backgroundColor: colors.surface,
        borderRadius: 8,
        boxShadow: shadows.card,
        gap: 10,
    },
    manageButtonText: {
        fontSize: 14,
        fontFamily: fonts.body,
        color: colors.text,
        flex: 1,
    },
    shortcutCount: {
        fontSize: 12,
        fontFamily: fonts.body,
        color: colors.textSecondary,
        marginTop: 8,
        textAlign: 'center',
    },
    resetButton: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: 12,
        gap: 8,
        marginTop: 8,
    },
    resetButtonText: {
        fontSize: 14,
        fontFamily: fonts.body,
        color: colors.error,
    },
}));

export default DashboardSettingsModal;
